import React, { useState, useRef, useEffect } from "react";
import emailjs from "emailjs-com";
import { Link } from "react-router-dom";

const initialState = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

// "+49 151 44375451" -> "+4915144375451" (für tel:-Links)
const telHref = (phone) => `tel:${String(phone).replace(/[^+\d]/g, "")}`;

// Bewusst nichtssagender Name: Browser und Passwortmanager füllen Felder
// wie "website", "url" oder "company" automatisch aus.
const HONEYPOT_FIELD = "hp_check";

export const Contact = (props) => {
  const [formState, setFormState] = useState(initialState);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const formRef = useRef(null);

  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE;
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE;
  const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_USER;

  useEffect(() => {
    if (PUBLIC_KEY) {
      emailjs.init(PUBLIC_KEY);
    }
  }, [PUBLIC_KEY]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const clearState = () => setFormState({ ...initialState });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Honeypot: von Menschen unsichtbar, Bots füllen es aus.
    // Der Feldname darf nichts sein, was Browser oder Passwortmanager
    // kennen (website, url, company ...) – sonst wird er automatisch
    // ausgefüllt und echte Anfragen werden stillschweigend verworfen.
    const honeypot = e.target.elements[HONEYPOT_FIELD];
    if (honeypot && honeypot.value) return;

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setStatus({
        type: "error",
        msg: "Das Formular ist derzeit nicht verfügbar. Bitte kontaktieren Sie uns telefonisch oder per E-Mail.",
      });
      return;
    }

    setSending(true);
    setStatus(null);

    emailjs
      .sendForm(SERVICE_ID, TEMPLATE_ID, e.target, PUBLIC_KEY)
      .then(() => {
        setStatus({
          type: "success",
          msg: "Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet.",
        });
        clearState();
        if (formRef.current) formRef.current.reset();
      })
      .catch((err) => {
        // Die Meldung von EmailJS muss in die Konsole, sonst ist der Grund
        // von aussen nicht zu erkennen. Der Fall, der uns das eingebrockt
        // hat: "Gmail_API: Invalid grant. Please reconnect your Gmail
        // account" – ein serverseitig abgelaufenes Token, das im Dashboard
        // erneuert werden muss und nichts mit dem Code zu tun hat.
        console.error(
          "EmailJS-Versand fehlgeschlagen:",
          (err && (err.text || err.message)) || err
        );
        setStatus({
          type: "error",
          msg: "Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie uns an.",
        });
      })
      .finally(() => setSending(false));
  };

  return (
    <div>
      <div id="contact">
        <div className="container">
          <div className="col-md-8">
            <div className="row">
              <div className="section-title">
                <h2>Kontaktieren Sie uns</h2>
                <p>
                  Füllen Sie das Formular unten aus, um uns eine Nachricht zu senden. Wir melden uns schnellstmöglich bei Ihnen.
                </p>
              </div>

              <form ref={formRef} name="contactForm" onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="sr-only" htmlFor="name">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        placeholder="Name"
                        autoComplete="name"
                        required
                        value={formState.name}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="sr-only" htmlFor="email">
                        E-Mail
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        placeholder="E-Mail"
                        autoComplete="email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label className="sr-only" htmlFor="phone">
                        Telefonnummer
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="form-control"
                        placeholder="Telefonnummer"
                        autoComplete="tel"
                        value={formState.phone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="sr-only" htmlFor="message">
                    Nachricht
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    className="form-control"
                    rows="4"
                    placeholder="Nachricht"
                    required
                    value={formState.message}
                    onChange={handleChange}
                  ></textarea>
                </div>

                {/* Spamschutz – für Nutzer unsichtbar.
                    data-lpignore und data-form-type halten 1Password,
                    LastPass & Co. davon ab, hier etwas einzutragen. */}
                <input
                  type="text"
                  name={HONEYPOT_FIELD}
                  tabIndex="-1"
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    opacity: 0,
                    height: 0,
                    width: 0,
                  }}
                />

                {status && (
                  <div
                    role="status"
                    aria-live="polite"
                    className={`alert ${status.type === "success" ? "alert-success" : "alert-danger"
                      }`}
                  >
                    {status.msg}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-custom btn-lg"
                  disabled={sending}
                >
                  {sending ? "Wird gesendet..." : "Nachricht senden"}
                </button>
              </form>
            </div>
          </div>

          {/* Kontaktinformationen */}
          <div className="col-md-3 col-md-offset-1 contact-info">
            <div className="contact-item">
              <h3>Kontaktinformationen</h3>
              <p>
                <span>
                  <i className="fa fa-map-marker" aria-hidden="true"></i> Adresse
                </span>
                {props.data ? props.data.address : "Lädt..."}
              </p>
            </div>
            <div className="contact-item">
              <p>
                <span>
                  <i className="fa fa-phone" aria-hidden="true"></i> Telefon
                </span>{" "}
                {props.data ? (
                  <a href={telHref(props.data.phone)}>{props.data.phone}</a>
                ) : (
                  "Lädt..."
                )}
              </p>
            </div>
            <div className="contact-item">
              <p>
                <span>
                  <i className="fa fa-envelope-o" aria-hidden="true"></i> E-Mail
                </span>{" "}
                {props.data ? (
                  <a href={`mailto:${props.data.email}`}>{props.data.email}</a>
                ) : (
                  "Lädt..."
                )}
              </p>
            </div>
          </div>

          <div className="col-md-3 col-md-offset-1 contact-info contact-info-rechtliches">
            <h3>Rechtliches</h3>
            <div className="contact-item rechtliches">
              <Link to="/impressum/" className="page-scroll">
                Impressum
              </Link>
            </div>
            <div className="contact-item rechtliches">
              <Link to="/agb/" className="page-scroll">
                AGBs
              </Link>
            </div>
          </div>

          <div className="col-md-12">
            <div className="row">
              <div className="social">
                <ul>
                  <li>
                    <a
                      href={props.data ? props.data.instagram : "/"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Merdovic Trockenbau auf Instagram"
                    >
                      <i className="fa fa-instagram" aria-hidden="true"></i>
                    </a>
                  </li>
                  <li>
                    <a
                      href={props.data ? props.data.whatsapp : "/"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Merdovic Trockenbau über WhatsApp kontaktieren"
                    >
                      <i className="fa fa-whatsapp" aria-hidden="true"></i>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="footer">
        <div className="container text-center">
          {/* Bewusst ein einziger Ausdruck statt "&copy; {jahr} Lanesa":
              gemischter Text und Platzhalter ergeben zwei benachbarte
              Textknoten, die beim Prerendering (scripts/prerender.mjs) zu
              einem verschmelzen – React meldet das beim Hydrieren als
              Fehler und baut den Bereich neu auf. */}
          <p>{`© ${new Date().getFullYear()} Lanesa Solutions`}</p>
        </div>
      </div>
    </div>
  );
};
